import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SaviBusinessJsonLd from "../../components/SaviBusinessJsonLd";
import SaviQuiz from "../../components/SaviQuiz";
import { ArrowRight, Wifi, Mountain, Monitor, Waves, Dumbbell, UtensilsCrossed } from "lucide-react";
import { pic } from "../../lib/pic";

const LuxuryColivingValenciaPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Luxury Coliving Valencia | Savi — Private Villa, Mountain Views</title>
        <meta name="description" content="Luxury coliving in Valencia, Spain. A private mountain villa with pool, coworking space, gym, and roof terrace. For founders and creatives. From €1,200/month." />
        <link rel="canonical" href="https://www.savicoliving.com/luxury-coliving-valencia" />
        <meta property="og:title" content="Luxury Coliving Valencia | Savi Coliving" />
        <meta property="og:description" content="A private villa in the mountains. Pool, gym, coworking, roof terrace. Not a hostel with a nice website." />
      </Helmet>
      <SaviBusinessJsonLd id="luxury-coliving-valencia" />

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
            <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
          </Link>
          <Link to="/" className="text-sm text-[#C85A38] font-medium hover:underline">View the house →</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-14">
        <div className="relative h-[70vh] overflow-hidden">
          <img {...pic("living-room")} fetchpriority="high" decoding="async" alt="Luxury coliving villa near Valencia Spain" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26] via-[#2E2A26]/50 to-[#2E2A26]/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4 max-w-4xl leading-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.5)'}}>
              Luxury coliving in Valencia<br className="hidden sm:block" /> that isn't a hotel.
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-xl mb-8" style={{textShadow: '0 2px 8px rgba(0,0,0,0.4)'}}>
              A private mountain villa with pool, coworking, gym, and roof terrace. The luxury is the environment — not the thread count.
            </p>
            <Link to="/">
              <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-4 text-sm font-medium tracking-wide transition-all">
                View the house <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* REDEFINE LUXURY */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">What luxury actually means here</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Let's be honest — when people search for "<strong className="text-[#2E2A26]/80">luxury coliving Valencia</strong>" they usually mean nice furniture and a cleaner bathroom. That's a low bar.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            The luxury at Savi is different. It's waking up to mountain views with no hangover. It's walking to the coworking space in your slippers. It's a pool that isn't shared with 200 hotel guests. It's dinner on the roof terrace with people who are genuinely interesting — because everyone here was selected to be.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            That's a level of intentionality you can't buy at a boutique hotel.
          </p>
        </div>
      </section>

      {/* THE PROPERTY */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">The property</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: Mountain, label: "Mountain villa", detail: "Private property in the Sierra Calderona" },
              { icon: Waves, label: "Pool", detail: "Private, surrounded by pine trees" },
              { icon: Monitor, label: "Coworking", detail: "Dual screens, standing desks, fast WiFi" },
              { icon: Dumbbell, label: "Gym", detail: "On-site equipment and outdoor training" },
              { icon: Wifi, label: "Starlink", detail: "Up to 500Mbps download speed" },
              { icon: UtensilsCrossed, label: "Full kitchen", detail: "Professional-grade, communal cooking" }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#C85A38]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#2E2A26] text-sm font-medium">{item.label}</p>
                  <p className="text-[#2E2A26]/40 text-xs">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTOS */}
      <section className="px-2 sm:px-4 py-2">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-2">
          <img {...pic("villa-front", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Villa exterior" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
          <img {...pic("room-suite", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Room interior" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
          <img {...pic("mountain-view", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Mountain view" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
          <img {...pic("living-room", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Living area" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
        </div>
      </section>

      {/* VS COMPARISON */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">Savi vs. what you're probably comparing</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            <strong className="text-[#2E2A26]/80">City coliving (Ruzafa, El Carmen):</strong> Convenient location, but noise, rotating guests, and communal spaces that never feel private. You're paying for a bed in someone's business, not a home.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            <strong className="text-[#2E2A26]/80">Boutique hotel with coworking:</strong> Nice, but expensive, transient, and lonely. You're a customer, not a resident. Nobody's cooking dinner together.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            <strong className="text-[#2E2A26]/80">Savi:</strong> A private property where you have space, community, and none of the compromise. Rooms from €1,200/month with everything included. The <strong className="text-[#2E2A26]/80">luxury coliving Valencia</strong> experience, without the corporate packaging.
          </p>
        </div>
      </section>

      {/* QUIZ */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-xl mx-auto text-center mb-8">
          <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-2">30 seconds</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Is Savi right for you?</h2>
          <p className="text-[#2E2A26]/50 text-sm">Four quick questions. No sign-up required.</p>
        </div>
        <SaviQuiz />
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-[#2E2A26]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">See it for yourself</h2>
          <p className="text-white/50 text-sm mb-8 max-w-md mx-auto">
            We keep it small — up to 16 guests at a time. Take a look at the rooms, the space, and the people. Then apply if it feels right.
          </p>
          <Link to="/">
            <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-12 py-5 text-sm font-medium transition-all">
              View Savi Coliving <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-8 px-4 bg-[#FBF7F1]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#2E2A26]/30 text-xs uppercase tracking-wider mb-3">Related</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/quiet-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Quiet Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/coliving-valencia-for-founders" className="text-[#C85A38] text-xs hover:underline">Coliving for Founders</Link>
          </div>
        </div>
      </section>

      <footer className="bg-white py-6 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/40 text-xs">&copy; 2026 SAVI Coliving</p>
          <div className="flex gap-4 text-xs text-[#2E2A26]/40">
            <Link to="/privacy" className="hover:text-[#2E2A26]">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#2E2A26]">Cookies</Link>
            <Link to="/" className="hover:text-[#2E2A26]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LuxuryColivingValenciaPage;
